"use client";

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, SearchX, Home, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MotionDiv } from '@/components/ui/motion';
import { useTenantContext } from '@/contexts/TenantContext';
import ImovelCard from '@/features/imoveis/ImovelCard';
import TenantHeader from '@/components/tenant/TenantHeader';
import TenantFooter from '@/components/tenant/TenantFooter';
import { TIPO_URL, TIPO_LABEL } from '@/lib/slug';

interface Property {
  id: string;
  title: string;
  description?: string | null;
  price: number;
  property_code?: string | null;
  property_type?: string | null;
  images?: string[] | null;
  transaction_type?: string | null;
  status?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  parking_spaces?: number | null;
  total_area?: number | null;
  built_area?: number | null;
  area_unit?: string | null;
  built_area_unit?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  building_name?: string | null;
}

interface RecommendationCriteria {
  propertyType?: string | null;
  bedrooms?: number | null;
  transactionType?: 'venda' | 'aluguel' | null;
  neighborhood?: string | null;
  city?: string | null;
}

/** "apartamento" → "apartamentos" (via mapas de slug; fallback: + "s") */
function pluralizeTipo(tipo: string): string {
  const urlSegment = TIPO_URL[tipo];
  if (urlSegment && TIPO_LABEL[urlSegment]) {
    return TIPO_LABEL[urlSegment].toLowerCase();
  }
  return `${tipo}s`;
}

/** Monta o headline contextual no padrão Zap: "Porque você navegou em apartamentos para venda com 3 quartos em Serrinha em Goiânia" */
function buildHeadline(criteria: RecommendationCriteria | null): string {
  if (!criteria || (!criteria.propertyType && !criteria.bedrooms && !criteria.transactionType && !criteria.neighborhood && !criteria.city)) {
    return 'Imóveis em destaque para você';
  }

  const parts: string[] = ['Porque você navegou em'];
  parts.push(criteria.propertyType ? pluralizeTipo(criteria.propertyType) : 'imóveis');
  if (criteria.transactionType) {
    parts.push(`para ${criteria.transactionType}`);
  }
  if (criteria.bedrooms) {
    parts.push(`com ${criteria.bedrooms} ${criteria.bedrooms === 1 ? 'quarto' : 'quartos'}`);
  }
  if (criteria.neighborhood) {
    parts.push(`em ${criteria.neighborhood}`);
  }
  if (criteria.city) {
    parts.push(`em ${criteria.city}`);
  }
  return parts.join(' ');
}

export default function TenantNotFound() {
  const { tenant, subdomain } = useTenantContext();
  const [properties, setProperties] = useState<Property[]>([]);
  const [criteria, setCriteria] = useState<RecommendationCriteria | null>(null);
  const [transactionType, setTransactionType] = useState<'venda' | 'aluguel' | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(4);

  // Estados para drag/swipe (mesma mecânica de ImoveisSimilar)
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const config = tenant?.configurations || {};
  const primary = config.cor_primaria || '#1B365D';

  // Responsividade: 1 card (mobile), 2 (tablet), 4 (desktop)
  useEffect(() => {
    const updateCardsPerView = () => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth < 768) {
        setCardsPerView(1);
      } else if (window.innerWidth < 1024) {
        setCardsPerView(2);
      } else {
        setCardsPerView(4);
      }
      setCurrentIndex(0);
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, []);

  // Buscar recomendações a partir da URL quebrada
  useEffect(() => {
    if (!subdomain) return;

    const fetchRecommendations = async () => {
      try {
        setLoading(true);

        // window.location.pathname é sempre a URL original do browser
        // (usePathname pode devolver o path reescrito /tenant/... do middleware)
        const segments = window.location.pathname.split('/').filter(Boolean);
        const lastSegment = segments.length > 0 ? decodeURIComponent(segments[segments.length - 1]) : '';

        const queryParams = new URLSearchParams({ limit: '8' });
        if (lastSegment) queryParams.set('slug', lastSegment.slice(0, 500));

        const response = await fetch(`/api/v1/imoveis/recommendations?${queryParams.toString()}`, {
          headers: { 'x-tenant-subdomain': subdomain }
        });

        if (!response.ok) {
          throw new Error('Erro ao buscar recomendações');
        }

        const data = await response.json();
        setProperties(data.properties || []);
        setCriteria(data.criteria || null);
        setTransactionType(data.criteria?.transactionType || null);
      } catch (error) {
        console.error('Erro ao buscar recomendações:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [subdomain]);

  // Navegação do carrossel
  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - cardsPerView));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      const maxIndex = properties.length - cardsPerView;
      return Math.min(maxIndex, prev + cardsPerView);
    });
  };

  const endDrag = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (carouselRef.current) {
      carouselRef.current.style.cursor = 'grab';
    }
    const threshold = 50;
    if (dragOffset > threshold) {
      handlePrevious();
    } else if (dragOffset < -threshold) {
      handleNext();
    }
    setDragOffset(0);
    setStartX(0);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setStartX(e.pageX);
    if (carouselRef.current) {
      carouselRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    e.preventDefault();
    setDragOffset(e.pageX - startX);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setDragOffset(e.touches[0].pageX - startX);
  };

  const visibleProperties = properties.slice(currentIndex, currentIndex + cardsPerView);
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < properties.length - cardsPerView;
  const totalPages = Math.ceil(properties.length / cardsPerView);
  const currentPage = Math.floor(currentIndex / cardsPerView);

  const imoveisHref = transactionType ? `/imoveis/${transactionType}` : '/imoveis';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {subdomain && <TenantHeader subdomain={subdomain} />}

      <main className="flex-1">
        {/* Hero 404 */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <div
            className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full"
            style={{ backgroundColor: `${primary}14` }}
          >
            <SearchX className="h-12 w-12" style={{ color: primary }} aria-hidden="true" />
          </div>
          <h1 className="text-3xl md:text-4xl font-light text-slate-800 mb-3">
            Oops. Não conseguimos encontrar a página solicitada.
          </h1>
          <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
            O imóvel pode ter sido vendido ou o endereço pode ter mudado.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild style={{ backgroundColor: primary, color: '#fff' }}>
              <Link href={imoveisHref}>
                <Building2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Ver todos os imóveis
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/">
                <Home className="mr-2 h-4 w-4" aria-hidden="true" />
                Voltar ao início
              </Link>
            </Button>
          </div>
        </section>

        {/* Recomendações */}
        {(loading || properties.length > 0) && (
          <section className="py-12 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
              <p className="text-base text-slate-600 mb-2">
                Mas separamos algumas recomendações que podem te interessar:
              </p>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-800">
                {loading ? 'Buscando sugestões para você...' : buildHeadline(criteria)}
              </h2>
            </div>

            {loading ? (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-96 bg-slate-200 animate-pulse rounded-xl" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative">
                  {canGoPrevious && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handlePrevious}
                      className="absolute left-2 md:left-0 lg:-left-14 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full w-10 h-10 md:w-12 md:h-12"
                      aria-label="Imóvel anterior"
                    >
                      <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                  )}

                  <div
                    ref={carouselRef}
                    className="px-12 md:px-14 lg:px-0 cursor-grab active:cursor-grabbing select-none"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={endDrag}
                    onMouseLeave={endDrag}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={endDrag}
                  >
                    <MotionDiv
                      key={currentIndex}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{
                        opacity: 1,
                        x: isDragging ? dragOffset : 0
                      }}
                      exit={{ opacity: 0, x: -50 }}
                      transition={{
                        duration: isDragging ? 0 : 0.4,
                        type: isDragging ? "tween" : "spring"
                      }}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                    >
                      {visibleProperties.map((property) => (
                        <div key={property.id}>
                          <ImovelCard imovel={property} priority={false} />
                        </div>
                      ))}
                    </MotionDiv>
                  </div>

                  {canGoNext && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleNext}
                      className="absolute right-2 md:right-0 lg:-right-14 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full w-10 h-10 md:w-12 md:h-12"
                      aria-label="Próximo imóvel"
                    >
                      <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="flex justify-center mt-8 gap-2">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i * cardsPerView)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          currentPage === i
                            ? 'w-8 bg-slate-800'
                            : 'w-2 bg-slate-300 hover:bg-slate-400'
                        }`}
                        aria-label={`Ir para página ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </main>

      {subdomain && <TenantFooter subdomain={subdomain} />}
    </div>
  );
}
